import { Page } from "../enums/Page";
import { css, html } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { ThrillerDanceService } from "../services/ThrillerDanceService";

const styles: string = css`
    :host {
        position: absolute;
        top: 7%;
    }
    
    #puzzle-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: 2%;
    }

    #image-container {
        display: flex;
        justify-content: center;
        align-items: end;
        gap: 10px;
        margin-bottom: 20px;
        height: 400px;
        width: 900px;
        background-image: url("/assets/img/mj-puzzle/thrillerstreet.png");
    }

    #check-button {
        font-family: 'Onesize';
        font-size: 20px;
        margin-top: 20px;
        cursor: pointer;
    }

    img {
        width: 80px;
        height: 100px;
        cursor: grab;
        margin: 5px;
    }

    .dragging {
        opacity: 0.5;
    }
    
    #instructions {
        font-family: 'Onesize';
        color: red;
        margin-bottom: 40px;
    }

    .alert {
        visibility: hidden;
        font-family: 'Onesize';
    }

    .alert.show {
        visibility: visible;
    }
`;
/**
 * A Web Component representing the Thriller Dance puzzle
 * @author Eva Biegel
 */
export class ThrillerDanceComponent extends HTMLElement {
    private _draggedItem: HTMLElement | null = null; // The image currently being dragged to swap with another item
    private _ThrillerDanceService: ThrillerDanceService = new ThrillerDanceService();
    private _gameEventService: GameEventService = new GameEventService();

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.render();
        this.shuffleImages();
    }

    private render(): void {
        if (!this.shadowRoot) return;

        const element: HTMLElement = this.renderPuzzle();

        this.shadowRoot.firstChild?.remove();
        this.shadowRoot.appendChild(element);

        this.initializeDragAndDrop();

        // Check if the answer is correct when the button is clicked
        const checkButton: HTMLButtonElement = this.shadowRoot.querySelector("#check-button")!;
        this.checkAnswer(checkButton);
    }

    /**
     * Renders the thriller dance puzzle component
     * @returns HTML element of the puzzle
     */
    private renderPuzzle(): HTMLElement {
        const element: HTMLElement = html`
            <div class="puzzle-div">
                <style>${styles}</style>
                <p id="instructions">Solve the puzzle by rearranging the images into the correct order</p>
                <div id="puzzle-container">
                    <div id="image-container">
                        <img src="/assets/img/mj-puzzle/mj1.png" class="sortable" draggable="true" data-id="mj1">
                        <img src="/assets/img/mj-puzzle/mj2.png" class="sortable" draggable="true" data-id="mj2">
                        <img src="/assets/img/mj-puzzle/mj3.png" class="sortable" draggable="true" data-id="mj3">
                        <img src="/assets/img/mj-puzzle/mj4.png" class="sortable" draggable="true" data-id="mj4">
                        <img src="/assets/img/mj-puzzle/mj3.png" class="sortable" draggable="true" data-id="mj3">
                        <img src="/assets/img/mj-puzzle/mj2.png" class="sortable" draggable="true" data-id="mj2">
                        <img src="/assets/img/mj-puzzle/mj4.png" class="sortable" draggable="true" data-id="mj4">
                        <img src="/assets/img/mj-puzzle/mj1.png" class="sortable" draggable="true" data-id="mj1">
                        <img src="/assets/img/mj-puzzle/mj4.png" class="sortable" draggable="true" data-id="mj4">
                    </div>
                    <button id="check-button">Check Answer</button>
                    <p class="alert"></p>
                </div>
            </div>
        `;

        return element;
    }

    /**
     * Initializes the drag and drop functionality for the puzzle, which allows the player to swap images to solve the puzzle
     */
    private initializeDragAndDrop(): void {
        const container: HTMLElement = this.shadowRoot!.querySelector("#image-container")!; // The div containing the images
        const images: NodeListOf<HTMLElement> = this.shadowRoot!.querySelectorAll(".sortable"); // The images to be sorted in the puzzle

        images.forEach(img => {
            img.addEventListener("dragstart", e => this.handleDragStart(e));
            img.addEventListener("dragover", e => e.preventDefault());
            img.addEventListener("drop", e => this.handleDrop(e));
            img.addEventListener("dragend", () => this.handleDragEnd());
        });

        container.addEventListener("dragover", e => e.preventDefault()); // enable the container to receive drop events
    }

    /**
     * Handles the drag start event for the images in the puzzle, meaning the player has started dragging an image
     * @param event
     */
    private handleDragStart(event: DragEvent): void {
        this._draggedItem = event.target as HTMLElement; // Set the draggedItem property to the current image being dragged
        this._draggedItem.classList.add("dragging"); // Add class to change opacity of image while dragging
    }

    /**
     * Handles moving the dragged image from one position to another in the puzzle when the player drops it
     * @param event
     */
    private handleDrop(event: DragEvent): void {
        event.preventDefault();
        const target: HTMLElement = event.target as HTMLElement; // The image that the dragged item is being dropped onto (destination image)

        // Check if the target is a sortable image and whether it's not the item currently being dragged
        if (this._draggedItem && target !== this._draggedItem && target.classList.contains("sortable")) {
            const parent: Node = target.parentNode!; // The parent of the target image, which is the image container
            const draggedNextSibling: ChildNode | null = this._draggedItem.nextSibling; // The next sibling of the dragged item
            parent.insertBefore(this._draggedItem, target); // Move the dragged image to the position of the target image
            parent.insertBefore(target, draggedNextSibling); // The target image is moved to the position of the dragged image
        }
    }

    /**
     * Handles the drag end event for the images in the puzzle, meaning the player has stopped dragging an image
     */
    private handleDragEnd(): void {
        if (this._draggedItem) {
            this._draggedItem.classList.remove("dragging");
            this._draggedItem = null; // Reset the draggedItem property to null after the player has stopped dragging an image
        }
    }

    /**
     * Checks if the player's answer is correct when the "Check Answer" button is clicked
     * @param checkButton
     */
    private checkAnswer(checkButton: HTMLButtonElement): void {
        checkButton.addEventListener("click", async () => {
            const images: NodeListOf<HTMLElement> = this.shadowRoot!.querySelectorAll(".sortable");
            const alert: Element = this.shadowRoot!.querySelector(".alert")!;

            // Get the data-id of each image in their current order
            const currentOrder: string[] = [];
            images.forEach(img => {
                currentOrder.push(img.dataset.id!);
            });

            const stringifyCurrentOrder: string = JSON.stringify(currentOrder); // Convert the current order to a string
            const isAnswerCorrect: boolean = await this._ThrillerDanceService.checkAnswer(stringifyCurrentOrder); // Check if the answer is correct

            if (isAnswerCorrect) {
                alert.textContent = "Wow! You're an amazing dancer!";
                alert.classList.add("show");
                setTimeout(() => {
                    this._gameEventService.switchPage(Page.Canvas);
                }, 1500);
            }
            else {
                alert.textContent = "Not quite right. Keep trying!";
                alert.classList.add("show");
                this.shuffleImages(); // Shuffle images when player gets the answer wrong and tries again
            }
            setTimeout(() => alert.classList.remove("show"), 1500); // Hide the alert message after 3 seconds
        });
    }

    /**
     * Shuffles the images in the puzzle to give the player a different starting order for each attempt
     */
    private shuffleImages(): void {
        const container: HTMLElement = this.shadowRoot!.querySelector("#image-container")!;
        const images: HTMLElement[] = Array.from(container.children) as HTMLElement[];

        for (let i: number = images.length - 1; i > 0; i--) {
            const j: number = Math.floor(Math.random() * (i + 1));
            [images[i], images[j]] = [images[j], images[i]];
        }

        // Clear the container and append the shuffled images
        container.innerHTML = "";
        images.forEach(img => container.appendChild(img));
    }
}
